import { tripChangeSnapshot } from '@koob/utils';

// Built from TO's Compose/store/useTripStore.js (US-039). `tripId` is not
// read by TO today — it joins the shape now so DMC's later adoption (US-043)
// is a same-shape swap, not a persisted-schema change.
export interface TripSlice {
  days: any[];
  experiences: Record<string, any>;
  hotels: Record<string, any>;
  trip: any;
  tripId: string | null;
  // The extras a hydrate carried in from the API before any per-day
  // `selectedExtras` processed them (US-047). Never persisted: it drains to
  // empty as each day component consumes its entries.
  initialExtras: any[];
  setInitialExtras: (extras: any[]) => void;
  setTrip: (trip: any) => void;
  initializeTrip: (
    tripData: {
      days?: any[];
      experiences?: Record<string, any>;
      hotels?: Record<string, any>;
    },
    tripId?: string | null,
  ) => void;
  resetTrip: () => void;
  updateDay: (dayIndex: number, updates: Record<string, any>) => void;
  updateDays: (days: any[]) => void;
  setExperience: (experienceId: string, experience: any) => void;
  setHotel: (hotelId: string, hotel: any) => void;
  updateHotelRoomSelection: (
    hotelId: string,
    roomData: any,
    dayIndex: number,
  ) => void;
  updateStayRoomsComposition: (dayIndex: number, roomsComposition: any) => void;
  isRoomSelectionExpired: (hotelId: string) => boolean;
  updateHotelPricing: (dayIndex: number, pricing: any) => void;
  updateExperiencePricing: (dayIndex: number, pricing: any) => void;
  addDay: (day: any) => void;
  removeDay: (dayIndex: number) => void;
  insertDay: (dayIndex: number, day: any) => void;
  reorderDays: (startIndex: number, endIndex: number) => void;
  setSelectedExtras: (updater: any) => void;
}

// Each action is a standalone factory (rather than inlined in the slice
// creator below) so that creator stays under the package's
// max-lines-per-function limit.
const createSetTrip = (set: any) => (trip: any) =>
  set({ trip: tripChangeSnapshot(trip) }, false, 'setTrip');

const createInitializeTrip = (set: any) => (tripData: any, tripId: any) =>
  set(
    {
      days: tripData?.days || [],
      experiences: tripData?.experiences || {},
      hotels: tripData?.hotels || {},
      tripId: tripId || null,
    },
    false,
    'initializeTrip',
  );

const createResetTrip = (set: any) => () =>
  set(
    {
      days: [],
      experiences: {},
      hotels: {},
      tripId: null,
      trip: null,
      initialExtras: [],
    },
    false,
    'resetTrip',
  );

const createSetInitialExtras = (set: any) => (extras: any[]) =>
  set({ initialExtras: extras }, false, 'setInitialExtras');

const createUpdateDay = (set: any) => (dayIndex: number, updates: any) =>
  set(
    (state: any) => ({
      days: state.days.map((day: any, idx: number) =>
        idx === dayIndex ? { ...day, ...updates } : day,
      ),
    }),
    false,
    'updateDay',
  );

const createUpdateDays = (set: any) => (days: any[]) =>
  set({ days }, false, 'updateDays');

const createSetExperience =
  (set: any) => (experienceId: string, experience: any) =>
    set(
      (state: any) => ({
        experiences: { ...state.experiences, [experienceId]: experience },
      }),
      false,
      'setExperience',
    );

// Nullish coalescing to handle explicit nulls distinctly from unset.
const createSetHotel = (set: any) => (hotelId: string, hotel: any) => {
  const updatedHotel = {
    ...hotel,
    selectedRoomKey: hotel.selectedRoomKey ?? null,
    selectedRoomData: hotel.selectedRoomData ?? null,
    roomsAvailableData: hotel.roomsAvailableData ?? null,
    roomSelectionExpiresAt: hotel.roomSelectionExpiresAt ?? null,
  };

  set(
    (state: any) => ({ hotels: { ...state.hotels, [hotelId]: updatedHotel } }),
    false,
    'setHotel',
  );
};

// Room selection is stored per day GROUP (on the day), not on the shared
// hotel object. Pricing is not this action's concern any more (US-058) —
// useTripHotelPricing reacts to the `selectedRoom` write below and prices it
// through `priceHotelStay`.
const createUpdateHotelRoomSelection =
  (set: any) => (hotelId: string, roomData: any, dayIndex: number) =>
    set(
      (state: any) => ({
        hotels: state.hotels,
        days: state.days.map((day: any, idx: number) =>
          idx === dayIndex
            ? {
                ...day,
                selectedRoom: {
                  roomKey: roomData.roomKey,
                  roomData: roomData.composition,
                  allRooms: roomData.allRooms,
                  availabilityData: roomData.availabilityData,
                  expiresAt: roomData.expiresAt,
                  requestId: roomData.requestId,
                },
                hotelSelectedRoomKey: null,
              }
            : day,
        ),
      }),
      false,
      'updateHotelRoomSelection',
    );

const createUpdateStayRoomsComposition =
  (set: any) => (dayIndex: number, roomsComposition: any) =>
    set(
      (state: any) => {
        const hotelId = state.days[dayIndex]?.hotelId;
        if (!hotelId) return state;

        let endIndex = dayIndex;
        while (state.days[endIndex + 1]?.hotelId === hotelId) endIndex++;

        return {
          days: state.days.map((day: any, idx: number) =>
            idx < dayIndex || idx > endIndex
              ? day
              : {
                  ...day,
                  roomsComposition: idx === dayIndex ? roomsComposition : null,
                  selectedRoom: null,
                  hotelPricing: null,
                  hotelSelectedRoomKey: null,
                },
          ),
        };
      },
      false,
      'updateStayRoomsComposition',
    );

const createIsRoomSelectionExpired = (get: any) => (hotelId: string) => {
  const hotel = get().hotels[hotelId];
  if (!hotel?.roomSelectionExpiresAt) return true;
  return new Date() > new Date(hotel.roomSelectionExpiresAt);
};

const createUpdateHotelPricing =
  (set: any) => (dayIndex: number, pricing: any) =>
    set(
      (state: any) => ({
        days: state.days.map((day: any, idx: number) =>
          idx === dayIndex ? { ...day, hotelPricing: pricing } : day,
        ),
      }),
      false,
      'updateHotelPricing',
    );

const createUpdateExperiencePricing =
  (set: any) => (dayIndex: number, pricing: any) =>
    set(
      (state: any) => ({
        days: state.days.map((day: any, idx: number) =>
          idx === dayIndex ? { ...day, experiencePricing: pricing } : day,
        ),
      }),
      false,
      'updateExperiencePricing',
    );

const createAddDay = (set: any) => (day: any) =>
  set((state: any) => ({ days: [...state.days, day] }), false, 'addDay');

const createRemoveDay = (set: any) => (dayIndex: number) =>
  set(
    (state: any) => ({
      days: state.days.filter((_: any, idx: number) => idx !== dayIndex),
    }),
    false,
    'removeDay',
  );

const createInsertDay = (set: any) => (dayIndex: number, day: any) =>
  set(
    (state: any) => ({
      days: [
        ...state.days.slice(0, dayIndex),
        day,
        ...state.days.slice(dayIndex),
      ],
    }),
    false,
    'insertDay',
  );

const createReorderDays =
  (set: any) => (startIndex: number, endIndex: number) =>
    set(
      (state: any) => {
        const result = Array.from(state.days);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return { days: result };
      },
      false,
      'reorderDays',
    );

// The extras selection lives on each day's own `selectedExtras` field, but
// consumers (built for DMC's cross-day filtering — a single multi-day
// experience's "global" extras are filed on its first day while its own
// component instance renders once per day) still want one flat,
// `dayIndex`-tagged list: flatten every day's array on read, hand it to
// `updater` exactly like a plain array setter would, then re-bucket the
// result back onto each day by its `dayIndex`.
const createSetSelectedExtras = (set: any) => (updater: any) =>
  set(
    (state: any) => {
      const currentFlat = state.days.flatMap((day: any) =>
        Array.isArray(day.selectedExtras) ? day.selectedExtras : [],
      );
      const nextFlat =
        typeof updater === 'function' ? updater(currentFlat) : updater;

      const byDayIndex: Record<number, any[]> = {};
      (Array.isArray(nextFlat) ? nextFlat : []).forEach((extra: any) => {
        const idx = extra?.dayIndex;
        if (idx == null) return;
        (byDayIndex[idx] ??= []).push(extra);
      });

      return {
        days: state.days.map((day: any, idx: number) => ({
          ...day,
          selectedExtras: byDayIndex[idx] ?? [],
        })),
      };
    },
    false,
    'setSelectedExtras',
  );

export const createTripSlice = (set: any, get: any): TripSlice => ({
  days: [],
  experiences: {},
  hotels: {},
  trip: null,
  tripId: null,
  initialExtras: [],

  setInitialExtras: createSetInitialExtras(set),
  setTrip: createSetTrip(set),
  initializeTrip: createInitializeTrip(set),
  resetTrip: createResetTrip(set),
  updateDay: createUpdateDay(set),
  updateDays: createUpdateDays(set),
  setExperience: createSetExperience(set),
  setHotel: createSetHotel(set),
  updateHotelRoomSelection: createUpdateHotelRoomSelection(set),
  updateStayRoomsComposition: createUpdateStayRoomsComposition(set),
  isRoomSelectionExpired: createIsRoomSelectionExpired(get),
  updateHotelPricing: createUpdateHotelPricing(set),
  updateExperiencePricing: createUpdateExperiencePricing(set),
  addDay: createAddDay(set),
  removeDay: createRemoveDay(set),
  insertDay: createInsertDay(set),
  reorderDays: createReorderDays(set),
  setSelectedExtras: createSetSelectedExtras(set),
});
